import React, { useEffect, useRef } from 'react';
import styles from './NotificationsPanel.module.scss';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import moment from 'moment';
import Loader from 'component/atom/loader/Loader';
import { usePost } from 'hooks/usePost';
import { notificationsEndpoints } from 'hooks/endpoints';
import { useDelete } from 'hooks/useDelete';
import NoData from 'component/molecules/Table/TableComponents/NoData/NoData';
import { useToast } from 'app-context/toasterContext/ToastContext';

const NotificationsPanel = ({
  open,
  onClose,
  notifications,
  isFetching,
  isLoading,
  refetchNotifications,
}) => {
  const toast = useToast();
  const panelRef = useRef(null);

  const { mutate: updateStatus } = usePost({
    hasToast: false,
  });
  const { mutate: clearAll, isPending: isPendingClearAll } = useDelete();

  const markAllAsRead = () => {
    notifications?.data?.items?.forEach((item) => {
      if (!item?.isRead) {
        updateStatus({
          url: notificationsEndpoints.updateStatus(item?.id),
          method: 'patch',
        });
      }
    });
  };

  const handleClearAll = () => {
    clearAll(
      {
        url: notificationsEndpoints.clearAll,
      },
      {
        onSuccess: (e) => {
          toast.open(e?.data?.userMessage, 'add');
          refetchNotifications();
        },
      },
    );
  };

  const handleClose = () => {
    markAllAsRead();
    onClose();
    refetchNotifications?.();
  };

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        handleClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, notifications]);

  return (
    <div
      ref={panelRef}
      className={`${styles['notifications-panel']} ${open ? styles['open'] : styles['closed']}`}
    >
      <div className={styles['notifications-header']}>
        <p className={styles['title']}>Notifications</p>
        <div className={styles['actions-container']}>
          {isPendingClearAll ? (
            <Loader style={{ width: '20px', height: '20px' }} />
          ) : (
            <p className={styles['clear-text']} onClick={handleClearAll}>
              Clear All
            </p>
          )}
          <SvgWithBranding
            type='exit'
            onClick={handleClose}
            className={styles['exit-img']}
            color='#011211'
          />
        </div>
      </div>

      <div className={styles['header-separator']}></div>

      <div className={styles['notifications-container']}>
        {isLoading ? (
          <Loader />
        ) : notifications?.data?.items?.length > 0 ? (
          notifications.data.items.map((item) => (
            <div
              key={item?.id}
              className={`${styles['notifications-item']} ${!item?.isRead ? styles['unread'] : ''}`}
            >
              <div className={styles['notification-icon']}>
                <SvgWithBranding
                  type={
                    item?.taskStatus === 0
                      ? 'success'
                      : item?.taskStatus === 1
                        ? 'failed-notification'
                        : item?.taskStatus === 2
                          ? 'warning-notification'
                          : item?.taskStatus === 3
                            ? 'update-notification'
                            : 'update-notification'
                  }
                />
              </div>

              {!item?.isRead && <div className={styles['unread-circle']}></div>}

              <div className={styles['info-container']}>
                <p className={styles['item-title']}>{item?.title}</p>
                <p className={styles['item-description']}>
                  {item?.description}
                </p>

                <div className={styles['date-botname']}>
                  <p className={styles['item-time']}>
                    {moment(item?.createdOn).fromNow()}
                  </p>
                  <p className={styles['meta-separator']}> - </p>
                  <p className={styles['botname']}>{item?.botName}</p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <NoData />
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;
